import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

class FloatingAddToCartBlock extends Component {
  requiredRefs = ['bar', 'addToCart'];

  #buyButtonsObserver = null;
  #footerObserver = null;
  #abortController = new AbortController();
  #targetAddToCartButton = null;
  #floatingAddToCartButton = null;
  #isStuck = false;
  #hiddenByFooter = false;
  #pendingTimeout = null;
  #addedTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupObservers();
    this.#bindFloatingAddToCart();

    const { signal } = this.#abortController;
    const section = this.#getProductSection();

    section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartComplete, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsObserver?.disconnect();
    this.#footerObserver?.disconnect();
    this.#abortController.abort();
    window.clearTimeout(this.#pendingTimeout);
    window.clearTimeout(this.#addedTimeout);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setupObservers();
    this.#bindFloatingAddToCart();
  }

  handleToggleOptions() {
    const panel = this.refs.optionsPanel;
    const toggle = this.refs.optionsToggle;
    if (!(panel instanceof HTMLElement) || !(toggle instanceof HTMLButtonElement)) return;

    const expanded = toggle.getAttribute('aria-expanded') !== 'true';
    toggle.setAttribute('aria-expanded', String(expanded));
    panel.hidden = !expanded;
  }

  handleOptionChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement || input instanceof HTMLSelectElement)) return;

    const optionValueId = input.dataset.optionValueId || input.value;
    const picker = this.#getVariantPicker();
    const mainOption = picker?.querySelector(`[data-option-value-id="${CSS.escape(optionValueId)}"]`);
    if (!(mainOption instanceof HTMLInputElement || mainOption instanceof HTMLOptionElement)) return;

    this.#setPending(true);

    if (mainOption instanceof HTMLInputElement) {
      if (mainOption.checked) {
        this.#setPending(false);
        return;
      }
      mainOption.click();
      return;
    }

    const select = mainOption.parentElement;
    if (!(select instanceof HTMLSelectElement)) return;
    if (select.value === mainOption.value) {
      this.#setPending(false);
      return;
    }
    select.value = mainOption.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #bindFloatingAddToCart() {
    const button = this.refs.addToCart.querySelector('[ref="addToCartButton"]');
    if (!(button instanceof HTMLButtonElement) || button === this.#floatingAddToCartButton) return;

    this.#floatingAddToCartButton = button;
    button.addEventListener('click', this.#handleAddToCart, { signal: this.#abortController.signal });
  }

  #handleAddToCart = (event) => {
    event.preventDefault();

    const targetButton = this.#getProductForm()?.querySelector('[ref="addToCartButton"]');
    if (!(targetButton instanceof HTMLButtonElement) || targetButton.disabled) return;

    const floatingQuantity = this.querySelector('quantity-selector-component');
    if (floatingQuantity?.getValue) this.#setMainQuantity(floatingQuantity.getValue());

    this.#setPending(true);
    targetButton.dataset.puppet = 'true';
    this.#animateAddToCart();
    targetButton.click();
  };

  /**
   * Mirrors the theme's native add-to-cart feedback for the proxy button.
   * The original button is marked as a puppet, so its component intentionally
   * skips the animation to prevent it from running off-screen.
   */
  #animateAddToCart = async () => {
    const button = this.#floatingAddToCartButton;
    if (!(button instanceof HTMLButtonElement)) return;

    button.dataset.added = 'true';
    window.clearTimeout(this.#addedTimeout);

    const container = this.refs.addToCart.querySelector('add-to-cart-component');
    const animationEnabled = container?.dataset.addToCartAnimation === 'true';
    const image = container?.dataset.productVariantMedia;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (animationEnabled && image && cartIcon instanceof Element) {
      const flyToCartElement = document.createElement('fly-to-cart');
      flyToCartElement.classList.add('fly-to-cart--main');
      flyToCartElement.style.setProperty('background-image', `url(${image})`);
      flyToCartElement.style.setProperty('--start-opacity', '0');
      flyToCartElement.source = button;
      flyToCartElement.destination = cartIcon;
      document.body.appendChild(flyToCartElement);

      await onAnimationEnd([button, flyToCartElement]);
    } else {
      await onAnimationEnd(button);
    }

    this.#addedTimeout = window.setTimeout(() => {
      button.removeAttribute('data-added');
    }, 800);
  };

  #setupObservers() {
    const productForm = this.#getProductForm();
    const buyButtons = productForm?.closest('.buy-buttons-block');
    if (!buyButtons) return;

    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    this.#buyButtonsObserver?.disconnect();
    this.#buyButtonsObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const { bottom } = entry.target.getBoundingClientRect();
        if (bottom < 0) this.#showBar();
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByFooter = false;
        this.#hideBar();
      }
    });
    this.#buyButtonsObserver.observe(buyButtons);

    if (this.dataset.hideAtFooter !== 'true') return;

    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    this.#footerObserver?.disconnect();
    this.#footerObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByFooter = true;
          this.#hideBar();
        } else if (!entry.isIntersecting && this.#hiddenByFooter) {
          const { bottom } = buyButtons.getBoundingClientRect();
          if (bottom < 0) {
            this.#hiddenByFooter = false;
            this.#showBar();
          }
        }
      },
      { rootMargin: '160px 0px 0px' }
    );
    this.#footerObserver.observe(footer);
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    this.#setPending(true);

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId } = detail;
        if (productId && productId !== this.dataset.productId) return;

        const selector = `floating-add-to-cart-block[data-instance-id="${CSS.escape(this.dataset.instanceId)}"]`;
        const updatedBlock = html.querySelector(selector);
        const updatedBar = updatedBlock?.querySelector('[ref="bar"]');
        if (!(updatedBar instanceof HTMLElement)) return;

        const visible = this.refs.bar.dataset.visible;
        const optionsPanel = this.refs.optionsPanel;
        const optionsOpen = optionsPanel instanceof HTMLElement && !optionsPanel.hidden;

        morph(this.refs.bar, updatedBar, { childrenOnly: true });
        this.refs.bar.dataset.visible = visible;

        const updatedPanel = this.refs.optionsPanel;
        const updatedToggle = this.refs.optionsToggle;
        if (updatedPanel instanceof HTMLElement) updatedPanel.hidden = !optionsOpen;
        if (updatedToggle instanceof HTMLButtonElement) updatedToggle.setAttribute('aria-expanded', String(optionsOpen));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[floating-add-to-cart-block] Product update failed:', error);
      })
      .finally(() => {
        this.#setupObservers();
        this.#syncQuantityFromMain();
        this.#setPending(false);
      });
  };

  #handleQuantityUpdate = (event) => {
    if (event.detail?.cartLine) return;

    const productForm = this.#getProductForm();
    const source = event.target instanceof Element ? event.target.closest('product-form-component') : null;
    if (source !== productForm) return;

    const floatingQuantity = this.querySelector('quantity-selector-component');
    if (floatingQuantity?.setValue) floatingQuantity.setValue(event.detail.quantity);
  };

  #handleCartComplete = (event) => {
    const reset = () => {
      if (this.#targetAddToCartButton) this.#targetAddToCartButton.dataset.puppet = 'false';
      this.#setPending(false);
    };

    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(reset);
    } else {
      reset();
    }
  };

  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    return this.#getProductSection()?.querySelector(`product-form-component[data-product-id="${productId}"]`) ?? null;
  }

  #getVariantPicker() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    return this.#getProductSection()?.querySelector(`variant-picker[data-product-id="${productId}"]`) ?? null;
  }

  #getProductSection() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const forms = document.querySelectorAll(`product-form-component[data-product-id="${productId}"]`);
    for (const form of forms) {
      const section = form.closest('.shopify-section');
      if (section?.querySelector('.product-information')) return section;
    }

    return forms[0]?.closest('.shopify-section') ?? null;
  }

  #setMainQuantity(quantity) {
    const selector = this.#getProductForm()?.querySelector('quantity-selector-component');
    if (selector?.setValue && Number.isFinite(Number(quantity))) selector.setValue(Number(quantity));
  }

  #syncQuantityFromMain() {
    const mainQuantity = this.#getProductForm()?.querySelector('quantity-selector-component');
    const floatingQuantity = this.querySelector('quantity-selector-component');
    if (mainQuantity?.getValue && floatingQuantity?.setValue) floatingQuantity.setValue(mainQuantity.getValue());
  }

  #showBar() {
    this.#isStuck = true;
    this.refs.bar.dataset.visible = 'true';
  }

  #hideBar() {
    this.#isStuck = false;
    this.refs.bar.dataset.visible = 'false';
  }

  #setPending(pending) {
    this.refs.bar.dataset.pending = String(pending);
    window.clearTimeout(this.#pendingTimeout);

    if (!pending) return;
    this.#pendingTimeout = window.setTimeout(() => {
      this.refs.bar.dataset.pending = 'false';
    }, 5000);
  }
}

if (!customElements.get('floating-add-to-cart-block')) {
  customElements.define('floating-add-to-cart-block', FloatingAddToCartBlock);
}
